import {
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDesign,
  IDisplayItem,
  IScreenDesign,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import { ActionSymbolWithChild } from "./action-symbol-with-child";

export interface IGetEntityAttributeValueInDynamicFormSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

export interface IGetEntityAttributeValueInDynamicFormSymbolsGroupState {}

class GetEntityAttributeValueInDynamicFormSymbolsGroup extends PureSdComponent<
  IGetEntityAttributeValueInDynamicFormSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IGetEntityAttributeValueInDynamicFormSymbolsGroupState
> {
  constructor(props: IGetEntityAttributeValueInDynamicFormSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    const { parameters, updateValues } = this.props;
    const diIdParam = parameters.find((p) => p.name === "diId");
    if (diIdParam && !diIdParam.symbol) {
      const newParams = _.cloneDeep(parameters);
      const targetParam = newParams.find((p) => p.name === "diId");
      if (targetParam) {
        targetParam.symbol = KMainFunctions.getDefaultSymbol(targetParam.Typename);
        updateValues({ actionParameters: newParams }, ESdChangeReason.DiActionUpdated);
      }
    } else if (!diIdParam) {
      const newParams = _.cloneDeep(parameters);
      newParams.push({
        name: "diId",
        Typename: "Int32",
        cardinality: 1,
        symbol: KMainFunctions.getDefaultSymbol("Int32")
      } as IActionParameter);
      updateValues({ actionParameters: newParams }, ESdChangeReason.DiActionUpdated);
    }

    const attributeIdParam = parameters.find((p) => p.name === "attributeId");
    if (attributeIdParam && !attributeIdParam.symbol) {
      const newParams = _.cloneDeep(parameters);
      const targetParam = newParams.find((p) => p.name === "attributeId");
      if (targetParam) {
        targetParam.symbol = KMainFunctions.getDefaultSymbol(targetParam.Typename);
        updateValues({ actionParameters: newParams }, ESdChangeReason.DiActionUpdated);
      }
    } else if (!attributeIdParam) {
      const newParams = _.cloneDeep(parameters);
      newParams.push({
        name: "attributeId",
        Typename: "Guid",
        cardinality: 1,
        symbol: KMainFunctions.getDefaultSymbol("Guid")
      } as IActionParameter);
      updateValues({ actionParameters: newParams }, ESdChangeReason.DiActionUpdated);
    }
  }

  handleComponentSelect = (value: string): void => {
    const { parameters, updateValues } = this.props;
    const newParams = _.cloneDeep(parameters);
    const diIdParam = newParams.find((p) => p.name === "diId");

    if (diIdParam) {
      diIdParam.symbol = {
        category: ESymbolCategory.fixed,
        fixedValue: parseInt(value, 10),
        currentType: "Int32",
        targetType: "Int32"
      } as ISymbol;
      updateValues({ actionParameters: newParams }, ESdChangeReason.ActionParameterFixedValueChanged);
    }
  };

  getSelectValue = (dynamicForms: IDisplayItem[]): string | undefined => {
    const diIdParam = this.props.parameters.find((p) => p.name === "diId");
    if (diIdParam?.symbol?.fixedValue !== undefined && diIdParam?.symbol?.fixedValue !== null) {
      return diIdParam.symbol.fixedValue.toString() === "0" ? undefined : diIdParam.symbol.fixedValue.toString();
    }
    return undefined;
  };

  // The row's value is the display item's own id, which is what the canvas highlights by.
  onMouseEnterComponent = (value: string | number | null): void => {
    if (value === null) return;
    this.props.sd.highlightHoveredItem(value.toString());
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (!di) return;
    result.push(di);
    di.children?.forEach((child) => {
      this.getAllDisplayItemsOfDi(child, result);
    });
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    sd?.displayItems?.forEach((di) => {
      this.getAllDisplayItemsOfDi(di, result);
    });
  };

  updateActionParameter = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const newParams = _.cloneDeep(this.props.parameters);
    const targetParam = newParams.find((p) => p.name === parameter.name);
    if (targetParam) {
      targetParam.symbol = symbol;
      this.props.updateValues({ actionParameters: newParams }, changeReason);
    }
  };

  renderComponentList = (): JSX.Element => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    this.fillAllDisplayItemsRecursive(sd, allDi);

    const dynamicForms = allDi
      .filter((di) => di.item.name === "DynamicForm")
      .sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName"));

    const dynamicFormOptions: KuikaSelectboxOption[] = dynamicForms.map((di: IDisplayItem): KuikaSelectboxOption => ({
      key: di.id.toString(),
      value: di.id.toString(),
      label: di.friendlyName
    }));

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Dynamic Form Component
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getSelectValue(dynamicForms) ?? null}
            onChange={this.handleComponentSelect}
            onOptionMouseEnter={this.onMouseEnterComponent}
            className="kuika_add_actions_inputs_size"
            placeholder="Select a Dynamic Form"
            options={dynamicFormOptions}
          />
        </div>
      </>
    );
  };

  renderAdditionalParameters = (): JSX.Element => {
    const attributeIdParam = this.props.parameters.find((p) => p.name === "attributeId");

    return (
      <>
        {attributeIdParam && (
          <ActionSymbolWithChild
            key={`actionSymbolWithChild-${this.props.action.id}-attributeId`}
            action={_.cloneDeep(this.props.action)}
            parameter={_.cloneDeep(attributeIdParam)}
            actionDefinition={null as any}
            actionPanelStatus={null as any}
            getSelectboxParameter={() => undefined}
            onActionSaved={() => {}}
            onActionParameterSelected={this.updateActionParameter}
            onDeleteParameter={() => {}}
          />
        )}
      </>
    );
  };

  render() {
    return (
      <div>
        {this.renderComponentList()}
        {this.renderAdditionalParameters()}
      </div>
    );
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: IGetEntityAttributeValueInDynamicFormSymbolsGroupProps & IWithSDProps & IWithContextProp
  ): boolean => {
    return !isEqual(this.props.action, nextProps.action) || !isEqual(this.props.parameters, nextProps.parameters);
  };
}

const getEntityAttributeValueInDynamicFormSymbolsGroup =
  withContext<IGetEntityAttributeValueInDynamicFormSymbolsGroupProps>(
    withSD<IGetEntityAttributeValueInDynamicFormSymbolsGroupProps>(GetEntityAttributeValueInDynamicFormSymbolsGroup)
  );
export { getEntityAttributeValueInDynamicFormSymbolsGroup as GetEntityAttributeValueInDynamicFormSymbolsGroup };
